import { Link } from "react-router-dom";
import { ShieldCheck, MapPin, Bot, CalendarClock, BookOpen, Building2, ArrowRight, Check } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50 grain">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home">
            <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-sky-400" />
            </div>
            <span className="font-display font-bold text-lg">HavenGuard</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-signin" className="text-sm px-4 py-2 text-slate-700 hover:text-slate-900">Sign in</Link>
            <Link to="/signup" data-testid="nav-getstarted" className="text-sm px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition">Get started</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Nationwide coverage — all 50 states + 20 major city ordinances
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Stop guessing.<br />
              <span className="text-sky-600">Start hosting</span> without the $20k fine.
            </h1>
            <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
              HavenGuard tracks every short-term rental regulation in your city — permits, night caps, occupancy taxes, safety rules — and gives you a clear compliance score for each property. Ask our AI anything about the law.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
        <Link to="/signup" data-testid="hero-primary-cta" className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-800 transition">
                Protect my rentals <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="hero-secondary-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-slate-700 border border-slate-300 hover:bg-white transition">
                I have an account
              </Link>
            </div>
            <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600 pt-3">
              {["Cited ordinance codes", "AI Q&A on local law", "Renewal reminders", "Fine risk calculator"].map((t) => (
                <li key={t} className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-600" />{t}</li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-sky-100 to-amber-100 rounded-3xl -z-10 blur-xl opacity-60" />
              <div className="bg-white border border-slate-200 rounded-2xl shadow-xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Portfolio Health</div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Live</span>
                </div>
                <div>
                  <div className="text-5xl font-bold font-display text-slate-900">87<span className="text-2xl text-slate-500">%</span></div>
                  <div className="text-sm text-slate-500">avg compliance across 4 properties</div>
                </div>
                <div className="grid grid-cols-3 gap-3 pt-2">
                  <div className="rounded-lg bg-emerald-50 p-3 border border-emerald-100">
                    <div className="text-xs text-emerald-700">Compliant</div>
                    <div className="text-2xl font-semibold text-emerald-800">18</div>
                  </div>
                  <div className="rounded-lg bg-amber-50 p-3 border border-amber-100">
                    <div className="text-xs text-amber-700">Warnings</div>
                    <div className="text-2xl font-semibold text-amber-800">3</div>
                  </div>
                  <div className="rounded-lg bg-rose-50 p-3 border border-rose-100">
                    <div className="text-xs text-rose-700">At Risk</div>
                    <div className="text-2xl font-semibold text-rose-800">1</div>
                  </div>
                </div>
                <div className="border-t border-slate-100 pt-4">
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Next Deadline</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-900">NYC OSE Registration Renewal</div>
                      <div className="text-xs text-slate-500">Manhattan Loft · Local Law 18</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">in</div>
                      <div className="text-lg font-semibold text-slate-900">14 days</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: MapPin, title: "Nationwide coverage", body: "All 50 states plus curated ordinances for NYC, LA, SF, Miami Beach, Chicago, DC, Denver, Seattle, Boston, Vegas, and 10+ more." },
            { icon: Building2, title: "Compliance per property", body: "Interactive checklist with document tracking, expiration dates, and status badges for every rule." },
            { icon: CalendarClock, title: "Never miss a renewal", body: "90-30-7 day reminders for permit renewals, tax filings, and safety inspections." },
            { icon: Bot, title: "AI regulatory Q&A", body: "Ask 'How many nights can I rent in LA?' — get answers backed by ordinance citations." },
            { icon: BookOpen, title: "Fine risk calculator", body: "See exactly how much a citation could cost — Miami Beach fines reach $20,000 per violation." },
            { icon: ShieldCheck, title: "Built for owners", body: "No corporate compliance jargon. Clear traffic-light statuses and plain-English rules." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition">
              <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-sky-400" />
              </div>
              <h3 className="font-display font-semibold text-slate-900 text-lg mb-2">{title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-slate-900 rounded-2xl p-8 sm:p-12 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Protect your listings today</h2>
          <p className="text-slate-300 mt-3 max-w-xl mx-auto">Add your properties, we'll load the local rules automatically. Free during beta.</p>
          <Link to="/signup" data-testid="footer-cta" className="inline-flex items-center gap-2 mt-6 bg-sky-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-sky-400 transition">
            Create free account <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="text-center text-xs text-slate-500 mt-8 font-mono">© 2026 HavenGuard · Not legal advice</div>
      </section>
    </div>
  );
}
